import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Card, Form, Button, Spinner, Accordion, Badge } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useQuery } from '@apollo/client'
import {
    useChannelConfig,
    ChannelConfigData,
    VicidialCampaignOption,
    fetchVicidialCampaignOptions,
} from './hooks/useChannelConfig'
import { useGetUser } from '../../store/userStore'
import { parseId } from '../../helpers'
import { GET_ALL_ORGANIZATIONS } from '../../gql/queries/organizationsQuery'
import { useAuth } from '../../../modules/auth'
import { STORAGE_NAMES } from '../../helpers/consts'

export const ChannelConfigPage = () => {
    const { auth } = useAuth()
    const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
    const currentUserOrgId = useGetUser((s) => (s.user?.user as any)?.organization?.id)
    const isSuperAdmin = userGroups.includes('SuperAdministrador')
    const isAdmin = userGroups.includes('Administrador') || isSuperAdmin

    // Organization selector state
    const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(undefined)
    const { data: orgData, loading: loadingOrgs } = useQuery(GET_ALL_ORGANIZATIONS, {
        variables: { first: 100 },
        skip: !isSuperAdmin,
    })
    const organizations = orgData?.allOrganizations?.edges?.map((e: any) => e.node) || []

    // Set initial org once loaded
    useEffect(() => {
        if (isSuperAdmin && organizations.length > 0 && !selectedOrgId) {
            // Default to current user's org, or first one
            const defaultOrg = currentUserOrgId || organizations[0]?.id
            setSelectedOrgId(defaultOrg)
        }
    }, [isSuperAdmin, organizations, selectedOrgId, currentUserOrgId])

    // Decode relay ID to numeric ID for the REST API
    const decodedOrgId = selectedOrgId ? String(parseId(selectedOrgId)) : undefined
    const { config, loading, saving, saveOrgConfig } = useChannelConfig(
        isSuperAdmin ? decodedOrgId : undefined
    )
    const [form, setForm] = useState<Partial<ChannelConfigData>>({})
    const [isDirty, setIsDirty] = useState(false)
    const [synced, setSynced] = useState(false)
    const [campaignOptions, setCampaignOptions] = useState<VicidialCampaignOption[]>([])
    const [loadingCampaigns, setLoadingCampaigns] = useState(false)

    // Sync form when config loads or org changes
    useEffect(() => {
        if (!loading) {
            setForm(config)
            setSynced(true)
            setIsDirty(false)
            setCampaignOptions([])
        }
    }, [config, loading])

    const getToken = useCallback(() => {
        return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
    }, [auth?.api_token])

    const visibleCampaignOptions = useMemo(() => {
        const currentCampaignId = String(form.vicidial_campaign_id || '').trim()
        if (!currentCampaignId || campaignOptions.some((campaign) => campaign.campaign_id === currentCampaignId)) {
            return campaignOptions
        }

        return [
            {
                campaign_id: currentCampaignId,
                campaign_name: 'Current organization default',
                active: 'Y',
            },
            ...campaignOptions,
        ]
    }, [campaignOptions, form.vicidial_campaign_id])

    const handleChange = (field: keyof ChannelConfigData, value: any) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setIsDirty(true)
    }

    const handleOrgChange = (orgId: string) => {
        setSelectedOrgId(orgId)
        setSynced(false)
        setIsDirty(false)
        setCampaignOptions([])
    }

    const handleLoadCampaigns = async () => {
        const token = getToken()
        if (!token) {
            toast.error('You must be logged in to load PolicyDial campaigns.')
            return
        }

        const baseUrl = String(form.vicidial_base_url || '').trim()
        const apiUser = String(form.vicidial_api_user || '').trim()
        const apiPassword = String(form.vicidial_api_password || '').trim()

        if (!baseUrl || !apiUser || !apiPassword) {
            toast.error('Base URL, API User and API Password are required to load PolicyDial campaigns.')
            return
        }

        setLoadingCampaigns(true)
        try {
            const campaigns = await fetchVicidialCampaignOptions({
                token,
                baseUrl,
                apiUser,
                apiPassword,
            })
            setCampaignOptions(campaigns)

            if (!campaigns.length) {
                toast.info('No PolicyDial campaigns were returned for this organization.')
                return
            }

            toast.success('PolicyDial campaigns loaded successfully.')
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Unable to load PolicyDial campaigns.'
            toast.error(message)
            setCampaignOptions([])
        } finally {
            setLoadingCampaigns(false)
        }
    }

    const handleSave = async () => {
        const success = await saveOrgConfig(form)
        if (success) {
            toast.success('Channel configuration saved successfully')
            setIsDirty(false)
        } else {
            toast.error('Error saving channel configuration')
        }
    }

    if (loading || (isSuperAdmin && loadingOrgs)) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" variant="primary" />
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="container-xxl py-5">
                <div className="alert alert-danger d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill fs-2 me-3"></i>
                    <div>
                        <h4 className="alert-heading">Permission Denied</h4>
                        <p className="mb-0">You do not have permission to configure organization channels. Please contact your administrator.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container-xxl py-5">
            <div className="d-flex justify-content-between align-items-center mb-6">
                <div>
                    <h1 className="fs-2 fw-bold text-gray-900 mb-1">Channel Configuration</h1>
                    <p className="text-muted fs-6 mb-0">
                        Configure communication channels for your organization
                    </p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    {config.webhook_token && (
                        <span className="text-muted fs-7">
                            Webhook: <code className="text-primary">{config.webhook_token.slice(0, 12)}...</code>
                        </span>
                    )}
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                        className="px-6"
                    >
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-check-lg me-1"></i>
                                Save Changes
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Organization Selector — SuperAdmin only */}
            {isSuperAdmin && (
                <Card className="mb-5 shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)' }}>
                    <Card.Body className="py-4">
                        <div className="d-flex align-items-center gap-4">
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-building fs-3 text-primary"></i>
                                <span className="fw-bold text-gray-800 fs-5">Organization</span>
                            </div>
                            <Form.Select
                                value={selectedOrgId || ''}
                                onChange={(e) => handleOrgChange(e.target.value)}
                                className="mw-400px"
                                style={{ borderColor: '#c5d2e8' }}
                            >
                                <option value="" disabled>Select organization...</option>
                                {organizations.map((org: any) => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {selectedOrgId && (
                                <Badge bg="light" text="dark" className="fs-7 px-3 py-2 border">
                                    Configuring: <strong>{organizations.find((o: any) => o.id === selectedOrgId)?.name || config.organization_name}</strong>
                                </Badge>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            )}

            <Accordion alwaysOpen>
                {/* ─── Telephony Provider ─── */}
                <Accordion.Item eventKey="telephony">
                    <Accordion.Header>
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-telephone-forward fs-4 text-primary"></i>
                            <span className="fw-bold fs-5">Telephony</span>
                            <Badge bg="light" text="dark" className="ms-2 border">
                                {form.telephony_provider || 'RINGCENTRAL'}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="row g-4">
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Active provider</Form.Label>
                                    <Form.Select
                                        value={form.telephony_provider || 'RINGCENTRAL'}
                                        onChange={(e) => handleChange('telephony_provider', e.target.value)}
                                    >
                                        <option value="RINGCENTRAL">RingCentral</option>
                                        <option value="VICIDIAL">PolicyDial</option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        Use this flag to migrate gradually. RingCentral can remain connected while you pilot PolicyDial.
                                    </Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <div className="alert alert-light-primary border border-primary border-dashed mb-0 h-100">
                                    <div className="fw-bold mb-1">Migration note</div>
                                    <div className="text-muted fs-7 mb-2">
                                        Shared PolicyDial credentials live here at organization level.
                                    </div>
                                    <div className="text-muted fs-7 mb-0">
                                        Per-user PolicyDial mappings can now be managed from the user profile <code>Channels</code> tab or while creating/editing the user.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ─── PolicyDial ─── */}
                <Accordion.Item eventKey="vicidial" className="mt-3">
                    <Accordion.Header>
                        <div className="d-flex align-items-center gap-2">
                            <img
                                src="/media/tfi/vicidial-icon.svg"
                                alt="PolicyDial"
                                style={{ width: '24px', height: '24px' }}
                            />
                            <span className="fw-bold fs-5">PolicyDial</span>
                            <Badge bg={form.vicidial_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.vicidial_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="row g-4">
                            <div className="col-12">
                                <Form.Check
                                    type="switch"
                                    id="vicidial_enabled"
                                    label="Enable PolicyDial telephony"
                                    checked={form.vicidial_enabled || false}
                                    onChange={(e) => handleChange('vicidial_enabled', e.target.checked)}
                                    className="fs-5"
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Base URL</Form.Label>
                                    <Form.Control
                                        type="url"
                                        placeholder="https://vici.policytools.com"
                                        value={form.vicidial_base_url || ''}
                                        onChange={(e) => handleChange('vicidial_base_url', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Webphone URL</Form.Label>
                                    <Form.Control
                                        type="url"
                                        placeholder="https://phone.viciphone.com/v3.0/viciphone.php"
                                        value={form.vicidial_webphone_url || ''}
                                        onChange={(e) => handleChange('vicidial_webphone_url', e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Used as fallback when the AGENT API cannot return the active session URL.
                                    </Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">API User</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="6666"
                                        value={form.vicidial_api_user || ''}
                                        onChange={(e) => handleChange('vicidial_api_user', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">API Password</Form.Label>
                                    <Form.Control
                                        type="text"
                                        autoComplete="off"
                                        placeholder="Enter API password"
                                        value={form.vicidial_api_password || ''}
                                        onChange={(e) => handleChange('vicidial_api_password', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Default agent user</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="support01"
                                        value={form.vicidial_agent_user || ''}
                                        onChange={(e) => handleChange('vicidial_agent_user', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Default phone code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="1"
                                        value={form.vicidial_phone_code || ''}
                                        onChange={(e) => handleChange('vicidial_phone_code', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Campaign</Form.Label>
                                    {visibleCampaignOptions.length > 0 ? (
                                        <Form.Select
                                            value={form.vicidial_campaign_id || ''}
                                            onChange={(e) => handleChange('vicidial_campaign_id', e.target.value)}
                                        >
                                            <option value=''>Select organization default campaign...</option>
                                            {visibleCampaignOptions.map((campaign) => (
                                                <option key={campaign.campaign_id} value={campaign.campaign_id}>
                                                    {campaign.campaign_id} - {campaign.campaign_name}
                                                    {campaign.active === 'Y' ? '' : ' (Inactive)'}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    ) : (
                                        <Form.Control
                                            type="text"
                                            placeholder="0001"
                                            value={form.vicidial_campaign_id || ''}
                                            onChange={(e) => handleChange('vicidial_campaign_id', e.target.value)}
                                        />
                                    )}
                                    <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                        <Button
                                            variant="light-primary"
                                            size="sm"
                                            onClick={handleLoadCampaigns}
                                            disabled={loadingCampaigns}
                                        >
                                            {loadingCampaigns ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Loading...
                                                </>
                                            ) : visibleCampaignOptions.length > 0 ? (
                                                'Refresh campaigns'
                                            ) : (
                                                'Load campaigns'
                                            )}
                                        </Button>
                                        <Form.Text className="text-muted mb-0">
                                            Super admins can set the organization default here. Users can still choose their active PolicyDial campaign from <code>Integrations</code>.
                                        </Form.Text>
                                    </div>
                                </Form.Group>
                            </div>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ─── Twilio SMS ─── */}
                <Accordion.Item eventKey="twilio" className="mt-3">
                    <Accordion.Header>
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-chat-dots fs-4 text-info"></i>
                            <span className="fw-bold fs-5">Twilio SMS</span>
                            <Badge bg={form.twilio_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.twilio_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="row g-4">
                            <div className="col-12">
                                <Form.Check
                                    type="switch"
                                    id="twilio_enabled"
                                    label="Enable Twilio SMS"
                                    checked={form.twilio_enabled || false}
                                    onChange={(e) => handleChange('twilio_enabled', e.target.checked)}
                                    className="fs-5"
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Account SID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                        value={form.twilio_account_sid || ''}
                                        onChange={(e) => handleChange('twilio_account_sid', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Auth Token</Form.Label>
                                    <Form.Control
                                        type="text"
                                        autoComplete="off"
                                        placeholder="Enter auth token"
                                        value={form.twilio_auth_token || ''}
                                        onChange={(e) => handleChange('twilio_auth_token', e.target.value)}
                                    />
                                    <Form.Text className="text-muted">Stored securely, not visible in API responses</Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Phone Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="+18586096799"
                                        value={form.twilio_phone_number || ''}
                                        onChange={(e) => handleChange('twilio_phone_number', e.target.value)}
                                    />
                                    <Form.Text className="text-muted">Include country code with +</Form.Text>
                                </Form.Group>
                            </div>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ─── WhatsApp Integration (Multi-Provider) ─── */}
                <Accordion.Item eventKey="whatsapp" className="mt-3">
                    <Accordion.Header>
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-whatsapp fs-4 text-success"></i>
                            <span className="fw-bold fs-5">WhatsApp Integration</span>
                            <Badge bg={form.whatsapp_provider !== 'NONE' ? 'success' : 'secondary'} className="ms-2">
                                {form.whatsapp_provider !== 'NONE' ? form.whatsapp_provider : 'Disabled'}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="row g-4 mb-4 pb-4 border-bottom">
                            <div className="col-12">
                                <Form.Group>
                                    <Form.Label className="fw-bold text-gray-800 fs-5 mb-3">Select WhatsApp Provider</Form.Label>
                                    <Form.Select
                                        value={form.whatsapp_provider || 'NONE'}
                                        onChange={(e) => handleChange('whatsapp_provider', e.target.value)}
                                        className="form-select-solid form-select-lg w-md-50"
                                    >
                                        <option value="NONE">Disabled</option>
                                        <option value="BOTMAKER">Botmaker</option>
                                        <option value="TWILIO">Twilio</option>
                                        <option value="META">Meta Cloud API</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        </div>

                        {/* Botmaker WhatsApp Config */}
                        {form.whatsapp_provider === 'BOTMAKER' && (
                            <div className="row g-4 animate__animated animate__fadeIn">
                                <div className="col-12 mb-2">
                                    <h5 className="fw-bold text-primary">Botmaker Configuration</h5>
                                    <p className="text-muted fs-7">Connect your Botmaker account to send and receive WhatsApp messages.</p>
                                </div>
                                <div className="col-12">
                                    <Form.Check
                                        type="switch"
                                        id="botmaker_enabled"
                                        label="Enable Botmaker Status (Legacy Sync)"
                                        checked={form.botmaker_enabled || false}
                                        onChange={(e) => handleChange('botmaker_enabled', e.target.checked)}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">API URL</Form.Label>
                                        <Form.Control
                                            type="url"
                                            placeholder="https://api.botmaker.com/v2.0"
                                            value={form.botmaker_api_url || ''}
                                            onChange={(e) => handleChange('botmaker_api_url', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Bot Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="thefutureinsurance"
                                            value={form.botmaker_bot_name || ''}
                                            onChange={(e) => handleChange('botmaker_bot_name', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-12">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">API Token (JWT)</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            autoComplete="off"
                                            rows={2}
                                            placeholder="eyJhbGciOiJIUzUxMiJ9..."
                                            value={form.botmaker_api_token || ''}
                                            onChange={(e) => handleChange('botmaker_api_token', e.target.value)}
                                            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                                        />
                                        <Form.Text className="text-muted">JWT token from Botmaker dashboard</Form.Text>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Default Channel ID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="thefutureinsurance-whatsapp-18586096799"
                                            value={form.botmaker_default_channel_id || ''}
                                            onChange={(e) => handleChange('botmaker_default_channel_id', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Phone Line Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="18586096799"
                                            value={form.botmaker_phone_line_number || ''}
                                            onChange={(e) => handleChange('botmaker_phone_line_number', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        )}

                        {/* Twilio WhatsApp Config */}
                        {form.whatsapp_provider === 'TWILIO' && (
                            <div className="row g-4 animate__animated animate__fadeIn">
                                <div className="col-12 mb-2">
                                    <h5 className="fw-bold text-primary">Twilio WhatsApp Configuration</h5>
                                    <p className="text-muted fs-7">Uses the Twilio Account SID and Auth Token configured in the Twilio SMS section.</p>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">WhatsApp Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="+18586096799"
                                            value={form.twilio_whatsapp_number || ''}
                                            onChange={(e) => handleChange('twilio_whatsapp_number', e.target.value)}
                                        />
                                        <Form.Text className="text-muted">Requires + prefix. Leave blank to use SMS number.</Form.Text>
                                    </Form.Group>
                                </div>
                            </div>
                        )}

                        {/* Meta WhatsApp Config */}
                        {form.whatsapp_provider === 'META' && (
                            <div className="row g-4 animate__animated animate__fadeIn">
                                <div className="col-12 mb-2">
                                    <h5 className="fw-bold text-primary">Meta Cloud API Configuration</h5>
                                    <p className="text-muted fs-7">Connect directly to Meta's WhatsApp Business API.</p>
                                </div>
                                <div className="col-12">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Access Token</Form.Label>
                                        <Form.Control
                                            type="password"
                                            placeholder="EAAL..."
                                            value={form.meta_whatsapp_access_token || ''}
                                            onChange={(e) => handleChange('meta_whatsapp_access_token', e.target.value)}
                                        />
                                        <Form.Text className="text-muted">Temporary or permanent access token</Form.Text>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Phone Number ID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="1234567890"
                                            value={form.meta_whatsapp_phone_number_id || ''}
                                            onChange={(e) => handleChange('meta_whatsapp_phone_number_id', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Verify Token</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="my_custom_verify_token"
                                            value={form.meta_whatsapp_verify_token || ''}
                                            onChange={(e) => handleChange('meta_whatsapp_verify_token', e.target.value)}
                                        />
                                        <Form.Text className="text-muted">Used for webhook validation</Form.Text>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Display Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="+1 858 609-6799"
                                            value={form.meta_whatsapp_phone_display || ''}
                                            onChange={(e) => handleChange('meta_whatsapp_phone_display', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        )}
                    </Accordion.Body>
                </Accordion.Item>

                {/* ─── Email SMTP ─── */}
                <Accordion.Item eventKey="email" className="mt-3">
                    <Accordion.Header>
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-envelope fs-4 text-warning"></i>
                            <span className="fw-bold fs-5">Email SMTP</span>
                            <Badge bg={form.email_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.email_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="row g-4">
                            <div className="col-12">
                                <Form.Check
                                    type="switch"
                                    id="email_enabled"
                                    label="Enable Email"
                                    checked={form.email_enabled || false}
                                    onChange={(e) => handleChange('email_enabled', e.target.checked)}
                                    className="fs-5"
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">SMTP Host</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="smtp.gmail.com"
                                        value={form.email_host || ''}
                                        onChange={(e) => handleChange('email_host', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-3">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Port</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="587"
                                        value={form.email_port || 587}
                                        onChange={(e) => handleChange('email_port', parseInt(e.target.value) || 587)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-3">
                                <Form.Group className="d-flex align-items-end h-100 pb-1">
                                    <Form.Check
                                        type="switch"
                                        id="email_use_tls"
                                        label="Use TLS"
                                        checked={form.email_use_tls !== false}
                                        onChange={(e) => handleChange('email_use_tls', e.target.checked)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">SMTP User</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="user@example.com"
                                        value={form.email_host_user || ''}
                                        onChange={(e) => handleChange('email_host_user', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">SMTP Password</Form.Label>
                                    <Form.Control
                                        type="text"
                                        autoComplete="off"
                                        placeholder="Enter SMTP password"
                                        value={form.email_host_password || ''}
                                        onChange={(e) => handleChange('email_host_password', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">From Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="notifications@example.com"
                                        value={form.email_from || ''}
                                        onChange={(e) => handleChange('email_from', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Reply-To Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="support@example.com"
                                        value={form.email_reply_to || ''}
                                        onChange={(e) => handleChange('email_reply_to', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ─── HealthSherpa Medicare ─── */}
                <Accordion.Item eventKey="healthsherpa" className="mt-3">
                    <Accordion.Header>
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-heart-pulse fs-4 text-danger"></i>
                            <span className="fw-bold fs-5">HealthSherpa Medicare</span>
                            <Badge bg={form.healthsherpa_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.healthsherpa_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="row g-4">
                            <div className="col-12">
                                <Form.Check
                                    type="switch"
                                    id="healthsherpa_enabled"
                                    label="Enable HealthSherpa Medicare"
                                    checked={form.healthsherpa_enabled || false}
                                    onChange={(e) => handleChange('healthsherpa_enabled', e.target.checked)}
                                    className="fs-5"
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Agent Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="agent@example.com"
                                        value={form.healthsherpa_email || ''}
                                        onChange={(e) => handleChange('healthsherpa_email', e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Must match a registered agent in HealthSherpa
                                    </Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">API Key</Form.Label>
                                    <Form.Control
                                        type="password"
                                        autoComplete="off"
                                        placeholder="Enter API key"
                                        value={form.healthsherpa_api_key || ''}
                                        onChange={(e) => handleChange('healthsherpa_api_key', e.target.value)}
                                    />
                                    <Form.Text className="text-muted">Stored securely, not visible after saving</Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-gray-700">Webhook Secret (X-API-Key)</Form.Label>
                                    <Form.Control
                                        type="password"
                                        autoComplete="off"
                                        placeholder="Secret key HealthSherpa sends to us"
                                        value={form.healthsherpa_webhook_secret || ''}
                                        onChange={(e) => handleChange('healthsherpa_webhook_secret', e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Value HealthSherpa sends in <code>X-API-Key</code> header on incoming webhooks. Agree with HealthSherpa support.
                                    </Form.Text>
                                </Form.Group>
                            </div>

                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {/* Webhook Info Card */}
            {config.webhook_token && (
                <Card className="mt-5 border-dashed border-primary bg-light-primary">
                    <Card.Body>
                        <div className="d-flex align-items-start gap-3">
                            <i className="bi bi-link-45deg fs-1 text-primary"></i>
                            <div className="w-100">
                                <h5 className="fw-bold text-gray-900 mb-2">Webhook URLs</h5>
                                <p className="text-muted mb-3 fs-7">
                                    Use these URLs to configure your external services (Botmaker, Twilio & PolicyDial).
                                </p>

                                {/* PolicyDial Dispo Webhook */}
                                {config.vicidial_enabled && (
                                    <div className="bg-white rounded p-3 mb-3 border border-gray-200">
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <small className="fw-bold text-gray-800">PolicyDial Dispo Call URL</small>
                                            <Badge bg="info" text="white">Disposition Sync</Badge>
                                        </div>
                                        <code className="text-primary fs-7 d-block text-break" style={{fontSize: '0.75rem'}}>
                                            VAR{window.location.origin.replace('3000', '8000').replace('3011', '8000')}/webhooks/vicidial/dispo/{config.webhook_token}/?uniqueid=--A--uniqueid--B--&call_id=--A--call_id--B--&phone=--A--phone_number--B--&dispo=--A--dispo--B--&talk_time=--A--talk_time--B--&user=--A--user--B--&campaign=--A--campaign--B--&term_reason=--A--term_reason--B--&recording_id=--A--recording_id--B--&call_notes=--A--call_notes--B--&vendor_lead_code=--A--vendor_lead_code--B--&lead_id=--A--lead_id--B--&dispo_epoch=--A--epoch--B--
                                        </code>
                                        <div className="mt-2 d-flex align-items-start gap-2">
                                            <i className="bi bi-info-circle text-info"></i>
                                            <small className="text-muted">
                                                Copy this URL into PolicyDial Admin &rarr; Campaign/In-Group &rarr; <strong>Dispo Call URL</strong> field.
                                                PolicyDial will call this URL automatically when an agent sets a disposition after each call.
                                            </small>
                                        </div>
                                    </div>
                                )}

                                {/* Botmaker Webhook */}
                                <div className="bg-white rounded p-3 mb-3 border border-gray-200">
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <small className="fw-bold text-gray-800">Botmaker Webhook</small>
                                        <Badge bg="light" text="dark" className="border">Standard</Badge>
                                    </div>
                                    <code className="text-primary fs-7 d-block text-break">
                                        {window.location.origin.replace('3000', '8000')}/webhooks/botmaker/{config.webhook_token}/
                                    </code>
                                </div>

                                {/* Twilio Webhook */}
                                <div className="bg-white rounded p-3 border border-gray-200">
                                    <WebhookUrlDisplay
                                        baseUrl={window.location.origin.replace('3000', '8000')}
                                        token={config.webhook_token}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Meta Webhook Info Card */}
            {config.webhook_token && form.whatsapp_provider === 'META' && (
                <Card className="mt-4 border-dashed border-success bg-light-success">
                    <Card.Body>
                        <div className="d-flex align-items-start gap-3">
                            <i className="bi bi-meta fs-1 text-success"></i>
                            <div className="w-100">
                                <h5 className="fw-bold text-gray-900 mb-2">Meta Webhook Configuration</h5>
                                <p className="text-muted mb-3 fs-7">
                                    Configure this URL in your Meta App Dashboard under WhatsApp Webhooks. Use the Verify Token from the configuration above.
                                </p>
                                <div className="bg-white rounded p-3 border border-gray-200">
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <small className="fw-bold text-gray-800">Callback URL</small>
                                    </div>
                                    <code className="text-success fs-7 d-block text-break">
                                        {window.location.origin.replace('3000', '8000')}/webhooks/meta-whatsapp/meta-whatsapp/
                                    </code>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    )
}

const WebhookUrlDisplay = ({ baseUrl, token }: { baseUrl: string, token: string }) => {
    const [mode, setMode] = useState<'standard' | 'messaging_service'>('messaging_service')

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <small className="fw-bold text-gray-800">Twilio Webhook</small>
                <div className="d-flex bg-light rounded p-1 border">
                    <button
                        className={`btn btn-sm py-1 px-3 border-0 ${mode === 'messaging_service' ? 'bg-white shadow-sm fw-bold text-primary' : 'text-gray-600'}`}
                        onClick={() => setMode('messaging_service')}
                        style={{ fontSize: '0.75rem', borderRadius: '4px' }}
                    >
                        Messaging Service
                    </button>
                    <button
                        className={`btn btn-sm py-1 px-3 border-0 ${mode === 'standard' ? 'bg-white shadow-sm fw-bold text-primary' : 'text-gray-600'}`}
                        onClick={() => setMode('standard')}
                        style={{ fontSize: '0.75rem', borderRadius: '4px' }}
                    >
                        Standard
                    </button>
                </div>
            </div>

            <code className="text-primary fs-7 d-block text-break mb-2">
                {mode === 'messaging_service'
                    ? `${baseUrl}/webhooks/twilio/`
                    : `${baseUrl}/webhooks/twilio/${token}/`
                }
            </code>

            {mode === 'messaging_service' && (
                <div className="alert alert-info d-flex align-items-center p-2 mb-0 mt-2" style={{ fontSize: '0.75rem' }}>
                    <i className="bi bi-info-circle-fill me-2 fs-6"></i>
                    <div>
                        <strong>Routing by Phone Number:</strong> Configures this URL in your Twilio Messaging Service.
                        Incoming messages will be routed to this organization based on the matched <strong>To</strong> number.
                    </div>
                </div>
            )}
            {mode === 'standard' && (
                <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                    <i className="bi bi-arrow-return-right me-1"></i>
                    Use this URL to configure a specific phone number directly (bypassing Messaging Service).
                </div>
            )}
        </div>
    )
}
